import React, { useEffect, useState } from "react";
import {
  Table,
  Input,
  Typography,
  Card,
  Tooltip,
  Button,
  Select,
  Tag,
  Form,
  message,
} from "antd";
import { FilterOutlined, ReloadOutlined, CopyOutlined, DownloadOutlined } from "@ant-design/icons";
import { Helmet } from "react-helmet";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import RequirePermission from "../components/Common/RequirePermission";
import ClearFiltersButton from "../components/Common/ClearFiltersButton";

const { Title } = Typography;

const DeviceErrors = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialParams = new URLSearchParams(location.search);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [currentPage, setCurrentPage] = useState(
    parseInt(initialParams.get("page"), 10) || 1
  );
  const [pageSize, setPageSize] = useState(
    parseInt(initialParams.get("limit"), 10) || 10
  );
  const [totalRecords, setTotalRecords] = useState(0);
  const [filterText, setFilterText] = useState(initialParams.get("filter") || "");
  const [deviceId, setDeviceId] = useState(
    initialParams.get("deviceId") ? parseInt(initialParams.get("deviceId"), 10) : null
  );
  const [errorType, setErrorType] = useState(initialParams.get("errorType") || null);
  const [showFilters, setShowFilters] = useState(
    Boolean(initialParams.get("deviceId") || initialParams.get("errorType"))
  );
  const [deviceOptions, setDeviceOptions] = useState([]);
  const [errorTypeOptions, setErrorTypeOptions] = useState([]);
  const [downloading, setDownloading] = useState(false);

  const fetchFilters = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/devices/error-logs/filters`
      );
      setDeviceOptions(response.data.data.devices || []);
      setErrorTypeOptions(response.data.data.errorTypes || []);
    } catch (error) {
      console.error("Error fetching device error filters", error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/devices/error-logs`,
        {
          params: {
            filter: filterText,
            limit: pageSize,
            page: currentPage,
            deviceId: deviceId || undefined,
            errorType: errorType || undefined,
          },
        }
      );
      setErrors(response.data.data.data);
      setTotalRecords(response.data.data.total);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching device error logs", error);
    }
  };

  useEffect(() => {
    fetchFilters();
  }, []);

  // Keeps the URL's query string in sync with every filter/pagination value,
  // the same pattern as the Signage TVs list (Device.jsx) - lets a filtered
  // view be bookmarked/shared/reloaded instead of resetting to defaults.
  useEffect(() => {
    const params = {
      filter: filterText || "",
      deviceId: deviceId || "",
      errorType: errorType || "",
      limit: pageSize,
      page: currentPage,
    };
    const searchParams = new URLSearchParams(params);
    navigate(`?${searchParams.toString()}`, { replace: true });

    const fetchTimer = setTimeout(() => {
      fetchData();
    }, 500);
    return () => clearTimeout(fetchTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, filterText, deviceId, errorType]);

  const handleSearch = (e) => {
    setFilterText(e.target.value);
    setCurrentPage(1);
  };

  const handleDeviceFilter = (value) => {
    setDeviceId(value);
    setCurrentPage(1);
  };

  const handleErrorTypeFilter = (value) => {
    setErrorType(value);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setDeviceId(null);
    setErrorType(null);
    setCurrentPage(1);
  };

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/devices/error-logs/download`,
        {
          params: {
            filter: filterText,
            deviceId: deviceId || undefined,
            errorType: errorType || undefined,
          },
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "device-errors.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setDownloading(false);
    } catch (error) {
      setDownloading(false);
      message.error("Failed to download device errors");
    }
  };

  const handleCopy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      message.success("Copied!");
    } catch (err) {
      message.error("Failed to copy");
      console.error("Clipboard error:", err);
    }
  };

  const truncatedTooltip = (text, max) => (
    <Tooltip title={text}>
      <span className="text-small">
        {text && text.length > max ? `${text.slice(0, max)}...` : text}
      </span>
    </Tooltip>
  );

  const columns = [
    {
      title: "Last Seen",
      dataIndex: "last_seen_at",
      render: (text) => dayjs(text).format("YYYY-MM-DD HH:mm:ss"),
      width: 170,
    },
    {
      title: "First Seen",
      dataIndex: "first_seen_at",
      render: (text) => dayjs(text).format("YYYY-MM-DD HH:mm:ss"),
      width: 170,
    },
    {
      title: "Count",
      dataIndex: "occurrence_count",
      width: 80,
      sorter: (a, b) => a.occurrence_count - b.occurrence_count,
      render: (count) => <Tag color={count > 1 ? "orange" : "default"}>{count}</Tag>,
    },
    {
      title: "Device",
      render: (_, record) => record.device_code || record.device_uuid || "Unknown",
    },
    {
      title: "Type",
      dataIndex: "error_type",
      render: (text) => (text ? <Tag>{text}</Tag> : "-"),
    },
    {
      title: "Message",
      dataIndex: "message",
      render: (text) => truncatedTooltip(text, 120),
    },
    {
      title: "Media URL",
      dataIndex: "video_url",
      render: (text) =>
        text ? (
          <span className="d-flex align-items-center">
            {truncatedTooltip(text, 40)}
            <Button
              icon={<CopyOutlined />}
              onClick={() => handleCopy(text)}
              size="small"
              className="ms-2"
            />
          </span>
        ) : (
          "-"
        ),
    },
    {
      title: "Page",
      dataIndex: "page_url",
      render: (text) => truncatedTooltip(text, 50),
    },
  ];

  const activeFilterCount = [deviceId, errorType].filter(Boolean).length;

  return (
    <RequirePermission permission="signage.device_errors_view">
      <Helmet>
        <title>{`${process.env.REACT_APP_NAME} | Device Errors`}</title>
      </Helmet>
      <Card>
        <Title level={3} style={{ marginBottom: "1rem" }}>
          Device Errors
        </Title>
        <div>
          <Form layout="inline">
            <Form.Item>
              <Input.Search
                placeholder="Search message, page, device..."
                value={filterText}
                onChange={handleSearch}
                style={{ width: 260 }}
                allowClear
              />
            </Form.Item>
            <Button
              icon={<FilterOutlined />}
              type={showFilters ? "primary" : "default"}
              onClick={() => setShowFilters((prev) => !prev)}
              style={{ marginRight: "1rem" }}
            >
              Filters
              {activeFilterCount > 0 && (
                <Tag color="blue" style={{ marginLeft: 6, marginRight: 0 }}>
                  {activeFilterCount}
                </Tag>
              )}
            </Button>
            <Form.Item>
              <Tooltip title="Refresh error logs">
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    fetchData();
                    fetchFilters();
                  }}
                  loading={loading}
                >
                  Refresh
                </Button>
              </Tooltip>
            </Form.Item>
            <Form.Item>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleDownload}
                loading={downloading}
              >
                Download Excel
              </Button>
            </Form.Item>
          </Form>
          <Form layout="inline" style={{ marginTop: "1rem" }}>
            {showFilters && (
              <Form.Item label="Device">
                <Select
                  placeholder="All devices"
                  value={deviceId}
                  onChange={handleDeviceFilter}
                  style={{ width: 220 }}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={deviceOptions.map((d) => ({
                    value: d.id,
                    label: d.code || d.id,
                  }))}
                />
              </Form.Item>
            )}
            {showFilters && (
              <Form.Item label="Error Type">
                <Select
                  placeholder="All types"
                  value={errorType}
                  onChange={handleErrorTypeFilter}
                  style={{ width: 220 }}
                  allowClear
                  options={errorTypeOptions.map((t) => ({ value: t, label: t }))}
                />
              </Form.Item>
            )}
            {showFilters && (
              <Form.Item style={{ marginLeft: "auto", alignSelf: "flex-end" }}>
                <ClearFiltersButton onClick={handleClearFilters} />
              </Form.Item>
            )}
          </Form>
        </div>
        <Table
          loading={loading}
          columns={columns}
          dataSource={errors}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalRecords,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
          className="mt-3"
          scroll={{ x: "max-content" }}
        />
      </Card>
    </RequirePermission>
  );
};

export default DeviceErrors;
